import { useState } from 'react';
import { Button } from 'reactstrap';
import { QUESTION } from '@constants/terminology';
import { useAppSelector } from '@reducers/index';
import { getRootConfig } from '@selectors/globalData';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';
import { useToggle } from '@hooks/useToggle';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { getNumericSelectedQuestions, getInputOverridableSelectedQuestions } from '@features/survey-question-list/utils';
import { MetricOverrideContainer } from '../metric-override/MetricOverrideContainer';
import { InputOverrideContainer } from '../input-override/InputOverrideContainer';
import { AggregationOverrideContainer } from '../aggregation-override/AggregationOverrideContainer';
import { TagContainer } from '@features/tags/TagContainer';
import { ConfigurationType } from '../types';
import { Menu } from '@g17eco/molecules/menu/Menu';
import { SelectedMetricsModal } from './SelectedMetricsModal';
import { getDescription } from '../modal/QuestionConfigurationModal';
import { FeatureStability } from '@g17eco/molecules/feature-stability';

interface MetricEditorPageProps {
  selectedQuestions: BulkActionUtr[];
  initiativeId: string;
  initialConfigType?: ConfigurationType;
  onNavigateBack: () => void;
  onReload?: () => void;
  rootInitiativeUtrMap: Map<string, InitiativeUniversalTracker>;
  utrTagMap: Record<string, string[]>;
}

export const MetricEditorPage = ({
  selectedQuestions,
  initiativeId,
  initialConfigType = ConfigurationType.MetricOverrides,
  onNavigateBack,
  onReload,
  rootInitiativeUtrMap,
  utrTagMap,
}: MetricEditorPageProps) => {
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessAggregationOverrides = FeaturePermissions.canAccessAggregationOverrides(rootConfig);
  const [activeTab, setActiveTab] = useState<ConfigurationType>(initialConfigType);
  const [isSelectedMetricsOpen, toggleSelectedMetrics] = useToggle(false);

  const isBulkMode = selectedQuestions.length > 1;
  const firstQuestion = selectedQuestions[0];

  const inputOverridableQuestions = getInputOverridableSelectedQuestions(selectedQuestions);
  const numericSelectedQuestions = getNumericSelectedQuestions(selectedQuestions);
  const handleReload = async ({ reloadSurvey = false, closeModal = false }: { reloadSurvey?: boolean; closeModal?: boolean; scrollToTop?: boolean } = {}) => {
    if (reloadSurvey) {
      await onReload?.();
    }
    if (closeModal) {
      onNavigateBack();
    }
  };

  const menuItems = [
    {
      label: ConfigurationType.MetricOverrides,
      active: activeTab === ConfigurationType.MetricOverrides,
      onClick: () => setActiveTab(ConfigurationType.MetricOverrides),
    },
    {
      label: ConfigurationType.InputOverrides,
      active: activeTab === ConfigurationType.InputOverrides,
      onClick: () => setActiveTab(ConfigurationType.InputOverrides),
      disabled: inputOverridableQuestions.length === 0,
    },
    {
      label: <>{ConfigurationType.AggregationOverrides} <FeatureStability stability='beta' /></>,
      active: activeTab === ConfigurationType.AggregationOverrides,
      onClick: () => setActiveTab(ConfigurationType.AggregationOverrides),
      disabled: selectedQuestions.length !== 1,
      hidden: !canAccessAggregationOverrides,
    },
    {
      label: ConfigurationType.Tags,
      active: activeTab === ConfigurationType.Tags,
      onClick: () => setActiveTab(ConfigurationType.Tags),
    },
  ];

  const title = isBulkMode
    ? `Bulk ${QUESTION.PLURAL} (${selectedQuestions.length} ${QUESTION.PLURAL})`
    : firstQuestion?.name ?? QUESTION.CAPITALIZED_SINGULAR;

  const reviewButton = isBulkMode ? (
    <Button outline onClick={toggleSelectedMetrics}>
      Review selected {QUESTION.PLURAL}
    </Button>
  ) : undefined;

  const configFormProps = {
    selectedQuestions,
    numericSelectedQuestions,
    initiativeId,
    handleReload,
    handleCloseModal: onNavigateBack,
    formActionsLeftContent: reviewButton,
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case ConfigurationType.MetricOverrides:
        return <MetricOverrideContainer {...configFormProps} />;
      case ConfigurationType.InputOverrides:
        return <InputOverrideContainer {...configFormProps} />;
      case ConfigurationType.AggregationOverrides:
        return (
          <AggregationOverrideContainer
            selectedQuestion={firstQuestion}
            initiativeId={initiativeId}
            handleReload={handleReload}
            handleCloseModal={onNavigateBack}
          />
        );
      case ConfigurationType.Tags:
        return (
          <>
            <TagContainer selectedQuestions={selectedQuestions} handleReload={handleReload} initiativeId={initiativeId} />
            {reviewButton ? (
              <div className='mt-3 pt-3 border-top'>{reviewButton}</div>
            ) : null}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <Button color='link' className='p-0 mb-2' onClick={onNavigateBack}>
        <i className='fa fa-chevron-left mr-2' />
        Back to {QUESTION.PLURAL}
      </Button>
      <h4 className='mb-3'>{title}</h4>
      <div className='text-ThemeTextMedium mb-3'>{getDescription({ configType: activeTab, isMultipleQuestionSelected: isBulkMode })}</div>
      <Menu items={menuItems} className='mb-3' />
      <div className='divider mb-3' />
      {renderTabContent()}
      {isBulkMode ? (
        <SelectedMetricsModal
          isOpen={isSelectedMetricsOpen}
          toggle={toggleSelectedMetrics}
          selectedQuestions={selectedQuestions}
          activeTab={activeTab}
          rootInitiativeUtrMap={rootInitiativeUtrMap}
          utrTagMap={utrTagMap}
        />
      ) : null}
    </div>
  );
};
